import React, { useState, useEffect } from 'react';
import {
  Gift,
  Users,
  DollarSign,
  Calendar,
  Send,
  Download,
  Upload,
  Filter,
  Search,
  Plus,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Award,
  Target,
  Zap,
  RefreshCw,
  FileText,
  BarChart3,
  TrendingUp,
  X,
  Save
} from 'lucide-react';
import voltronWalletService, { VoltronWallet } from '../../services/voltronWalletService';

interface BonusRecord {
  id: string;
  name: string;
  type: 'PERFORMANCE' | 'REFERRAL' | 'MILESTONE' | 'SPECIAL' | 'HOLIDAY' | 'LOYALTY';
  recipients: string[];
  amount: number;
  currency: 'VTTN' | 'USDT';
  totalAmount: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  vestingMonths?: number;
  reason: string;
  criteria?: string;
  createdBy: string;
  createdAt: string;
  executedAt?: string;
  txHash?: string;
}

interface BonusStats {
  totalDistributed: number;
  totalRecipients: number;
  pendingAmount: number;
  completedBonuses: number;
  averageBonus: number;
  topPerformer: string;
}

const BonusManagementPage: React.FC = () => {
  const [bonuses, setBonuses] = useState<BonusRecord[]>([]);
  const [stats, setStats] = useState<BonusStats>({
    totalDistributed: 0,
    totalRecipients: 0,
    pendingAmount: 0,
    completedBonuses: 0,
    averageBonus: 0,
    topPerformer: ''
  });
  const [wallets, setWallets] = useState<VoltronWallet[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWalletSelector, setShowWalletSelector] = useState(false);
  const [selectedWallets, setSelectedWallets] = useState<string[]>([]);
  
  const [bonusForm, setBonusForm] = useState({
    name: '',
    type: 'PERFORMANCE' as BonusRecord['type'],
    amount: '',
    currency: 'VTTN' as 'VTTN' | 'USDT',
    reason: '',
    criteria: '',
    vestingMonths: '0',
    recipients: [] as string[]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 지갑 리스트 로드
      const walletsData = await voltronWalletService.getAllWallets();
      setWallets(walletsData);
      
      // 보너스 내역 로드 (Mock 데이터)
      setBonuses(getMockBonuses());
      setStats(getMockStats());
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBonus = async () => {
    try {
      setLoading(true);
      
      const bonusData = {
        recipients: bonusForm.recipients,
        amount: parseFloat(bonusForm.amount),
        currency: bonusForm.currency,
        reason: bonusForm.reason,
        vestingMonths: parseInt(bonusForm.vestingMonths) || 0
      };
      
      const result = await voltronWalletService.distributeBonus(bonusData);
      
      if (result.success) {
        alert(`보너스가 성공적으로 지급되었습니다!\nTx Hash: ${result.txHash}`);
        setShowCreateModal(false);
        setBonusForm({
          name: '',
          type: 'PERFORMANCE',
          amount: '',
          currency: 'VTTN',
          reason: '',
          criteria: '',
          vestingMonths: '0',
          recipients: []
        });
        await loadData();
      } else {
        alert(`보너스 지급에 실패했습니다: ${result.error}`);
      }
    } catch (error) {
      console.error('Bonus distribution failed:', error);
      alert('보너스 지급 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletSelection = (walletAddress: string) => {
    setSelectedWallets(prev => {
      if (prev.includes(walletAddress)) {
        return prev.filter(addr => addr !== walletAddress);
      } else {
        return [...prev, walletAddress];
      }
    });
  };

  const handleSelectAllWallets = () => {
    if (selectedWallets.length === wallets.length) {
      setSelectedWallets([]);
    } else {
      setSelectedWallets(wallets.map(w => w.walletAddress));
    }
  };

  const handleApplyWalletSelection = () => {
    setBonusForm({
      ...bonusForm,
      recipients: selectedWallets
    });
    setShowWalletSelector(false);
  };

  const getMockBonuses = (): BonusRecord[] => {
    return [
      {
        id: '1',
        name: '2024년 1월 성과 보너스',
        type: 'PERFORMANCE',
        recipients: ['0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C1', '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2'],
        amount: 1000,
        currency: 'VTTN',
        totalAmount: 2000,
        status: 'COMPLETED',
        reason: '월간 성과 목표 달성',
        criteria: '스테이킹 참여 + 노드 운영',
        createdBy: 'admin@voltron.io',
        createdAt: '2024-01-20T10:00:00Z',
        executedAt: '2024-01-20T10:05:00Z',
        txHash: '0xbonus1234567890abcdef'
      },
      {
        id: '2',
        name: '추천 보너스',
        type: 'REFERRAL',
        recipients: ['0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C1'],
        amount: 500,
        currency: 'USDT',
        totalAmount: 500,
        status: 'PENDING',
        vestingMonths: 3,
        reason: '신규 투자자 추천',
        criteria: '5명 이상 추천 성공',
        createdBy: 'admin@voltron.io',
        createdAt: '2024-01-20T14:00:00Z'
      }
    ];
  };

  const getMockStats = (): BonusStats => {
    return {
      totalDistributed: 125000,
      totalRecipients: 89,
      pendingAmount: 15000,
      completedBonuses: 23,
      averageBonus: 1404,
      topPerformer: '투자자A'
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'text-green-600 bg-green-100';
      case 'PROCESSING': return 'text-blue-600 bg-blue-100';
      case 'PENDING': return 'text-yellow-600 bg-yellow-100';
      case 'FAILED': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'PERFORMANCE': return 'text-blue-600 bg-blue-100';
      case 'REFERRAL': return 'text-green-600 bg-green-100';
      case 'MILESTONE': return 'text-purple-600 bg-purple-100';
      case 'SPECIAL': return 'text-orange-600 bg-orange-100';
      case 'HOLIDAY': return 'text-red-600 bg-red-100';
      case 'LOYALTY': return 'text-indigo-600 bg-indigo-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredBonuses = bonuses.filter(bonus => {
    const matchesSearch = bonus.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bonus.reason.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || bonus.status === statusFilter;
    const matchesType = typeFilter === 'all' || bonus.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">보너스 관리</h1>
          <p className="text-gray-600 mt-1">투자자 및 참여자 보너스 지급을 관리하세요</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadData}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            <span>새로고침</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <Plus className="w-4 h-4" />
            <span>보너스 지급</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">총 지급액</p>
              <p className="text-2xl font-bold text-green-600">{stats.totalDistributed.toLocaleString()}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">수혜자 수</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalRecipients}</p>
              <p className="text-xs text-gray-500">명</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">대기 중</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingAmount.toLocaleString()}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">완료 건수</p>
              <p className="text-2xl font-bold text-purple-600">{stats.completedBonuses}</p>
              <p className="text-xs text-gray-500">건</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">평균 보너스</p>
              <p className="text-2xl font-bold text-indigo-600">{stats.averageBonus.toLocaleString()}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
            <div className="p-2 bg-indigo-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">최고 수혜자</p>
              <p className="text-lg font-bold text-orange-600">{stats.topPerformer}</p>
              <p className="text-xs text-gray-500">Top Performer</p>
            </div>
            <div className="p-2 bg-orange-100 rounded-lg">
              <Star className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">검색</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="보너스명, 사유 검색..."
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">상태</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">전체</option>
              <option value="PENDING">대기 중</option>
              <option value="PROCESSING">처리 중</option>
              <option value="COMPLETED">완료</option>
              <option value="FAILED">실패</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">유형</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">전체</option>
              <option value="PERFORMANCE">성과 보너스</option>
              <option value="REFERRAL">추천 보너스</option>
              <option value="MILESTONE">마일스톤</option>
              <option value="SPECIAL">특별 보너스</option>
              <option value="HOLIDAY">휴일 보너스</option>
              <option value="LOYALTY">충성도 보너스</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center justify-center space-x-2">
              <Download className="w-4 h-4" />
              <span>내보내기</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bonus List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">보너스명</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">유형</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">수혜자</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">금액</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">생성일</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    로딩 중...
                  </td>
                </tr>
              ) : filteredBonuses.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    보너스 내역이 없습니다.
                  </td>
                </tr>
              ) : (
                filteredBonuses.map((bonus) => (
                  <tr key={bonus.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{bonus.name}</div>
                        <div className="text-sm text-gray-500">{bonus.reason}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(bonus.type)}`}>
                        {bonus.type === 'PERFORMANCE' ? '성과' :
                         bonus.type === 'REFERRAL' ? '추천' :
                         bonus.type === 'MILESTONE' ? '마일스톤' :
                         bonus.type === 'SPECIAL' ? '특별' :
                         bonus.type === 'HOLIDAY' ? '휴일' : '충성도'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bonus.recipients.length}명
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {bonus.totalAmount.toLocaleString()} {bonus.currency}
                      </div>
                      <div className="text-sm text-gray-500">
                        개당 {bonus.amount.toLocaleString()} {bonus.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bonus.status)}`}>
                        {bonus.status === 'COMPLETED' ? '완료' :
                         bonus.status === 'PROCESSING' ? '처리 중' :
                         bonus.status === 'PENDING' ? '대기 중' : '실패'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(bonus.createdAt).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button className="text-blue-600 hover:text-blue-900">
                          <Eye className="w-4 h-4" />
                        </button>
                        {bonus.txHash && (
                          <button 
                            onClick={() => window.open(`/tx/${bonus.txHash}`, '_blank')}
                            className="text-green-600 hover:text-green-900"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Bonus Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Gift className="w-6 h-6 text-orange-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">보너스 지급</h3>
                    <p className="text-gray-500 text-sm">투자자 및 참여자에게 보너스를 지급합니다</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-6">
                {/* 기본 정보 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">보너스명</label>
                    <input
                      type="text"
                      value={bonusForm.name}
                      onChange={(e) => setBonusForm({...bonusForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="2024년 1월 성과 보너스"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">보너스 유형</label>
                    <select
                      value={bonusForm.type}
                      onChange={(e) => setBonusForm({...bonusForm, type: e.target.value as BonusRecord['type']})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="PERFORMANCE">성과 보너스</option>
                      <option value="REFERRAL">추천 보너스</option>
                      <option value="MILESTONE">마일스톤 보너스</option>
                      <option value="SPECIAL">특별 보너스</option>
                      <option value="HOLIDAY">휴일 보너스</option>
                      <option value="LOYALTY">충성도 보너스</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">개당 지급액</label>
                    <input
                      type="text"
                      value={bonusForm.amount}
                      onChange={(e) => setBonusForm({...bonusForm, amount: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="1000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">통화</label>
                    <select
                      value={bonusForm.currency}
                      onChange={(e) => setBonusForm({...bonusForm, currency: e.target.value as 'VTTN' | 'USDT'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="VTTN">VTTN</option>
                      <option value="USDT">USDT</option>
                    </select>
                  </div>
                </div>

                {/* 수혜자 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">수혜자 선택</label>
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-600">
                        선택된 지갑: {bonusForm.recipients.length}개
                      </span>
                      <button
                        onClick={() => setShowWalletSelector(true)}
                        className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                      >
                        지갑 선택
                      </button>
                    </div>
                    {bonusForm.recipients.length > 0 && (
                      <div className="max-h-32 overflow-y-auto">
                        {bonusForm.recipients.map((address, index) => (
                          <div key={index} className="text-xs text-gray-500 mb-1">
                            {address}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* 베스팅 설정 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">베스팅 기간 (개월)</label>
                  <input
                    type="text"
                    value={bonusForm.vestingMonths}
                    onChange={(e) => setBonusForm({...bonusForm, vestingMonths: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0 (즉시 지급)"
                  />
                  <p className="text-xs text-gray-500 mt-1">0이면 즉시 지급, 1 이상이면 해당 개월 동안 분할 지급</p>
                </div>

                {/* 사유 및 기준 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">지급 사유</label>
                  <textarea
                    value={bonusForm.reason}
                    onChange={(e) => setBonusForm({...bonusForm, reason: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    rows={3}
                    placeholder="보너스 지급 사유를 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">지급 기준</label>
                  <textarea
                    value={bonusForm.criteria}
                    onChange={(e) => setBonusForm({...bonusForm, criteria: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    rows={2}
                    placeholder="보너스 지급 기준을 입력하세요 (선택사항)"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                취소
              </button>
              <div className="flex space-x-3">
                <div className="text-sm text-gray-600">
                  총 지급액: {bonusForm.recipients.length > 0 && bonusForm.amount ? 
                    (parseFloat(bonusForm.amount) * bonusForm.recipients.length).toLocaleString() : '0'} {bonusForm.currency}
                </div>
                <button
                  onClick={handleCreateBonus}
                  disabled={loading || !bonusForm.name || !bonusForm.amount || bonusForm.recipients.length === 0}
                  className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                >
                  {loading ? '지급 중...' : '보너스 지급'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Selector Modal */}
      {showWalletSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">지갑 선택</h3>
                <button
                  onClick={() => setShowWalletSelector(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSelectAllWallets}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    {selectedWallets.length === wallets.length ? '전체 해제' : '전체 선택'}
                  </button>
                  <span className="text-sm text-gray-600">
                    {selectedWallets.length}/{wallets.length} 선택됨
                  </span>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">선택</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">지갑 주소</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">닉네임</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">등급</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">잔액</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {wallets.map((wallet) => (
                      <tr key={wallet.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={selectedWallets.includes(wallet.walletAddress)}
                            onChange={() => handleWalletSelection(wallet.walletAddress)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                        </td>
                        <td className="px-4 py-2 text-sm font-mono text-gray-900">
                          {wallet.walletAddress.slice(0, 10)}...{wallet.walletAddress.slice(-8)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {wallet.nickname || '미설정'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            wallet.tier === 'DIAMOND' ? 'bg-purple-100 text-purple-800' :
                            wallet.tier === 'PLATINUM' ? 'bg-gray-100 text-gray-800' :
                            wallet.tier === 'GOLD' ? 'bg-yellow-100 text-yellow-800' :
                            wallet.tier === 'SILVER' ? 'bg-gray-100 text-gray-600' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {wallet.tier}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {wallet.vttnBalance.toLocaleString()} VTTN
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                선택된 지갑: {selectedWallets.length}개
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowWalletSelector(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  취소
                </button>
                <button
                  onClick={handleApplyWalletSelection}
                  className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  선택 완료
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BonusManagementPage;